import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Button, Card, PageHeader, Badge, Modal, Input, Select, EmptyState } from '../../components/ui/UI';
import { Plus, Copy, Trash2, Pencil, Users, Link2, RefreshCw } from 'lucide-react';
import './ObserversPage.css';

const BUS = ['SNACKS', 'DAIRIES', 'SWAN'];
const DEPTS = ['Human Resources', 'Engineering', 'Finance', 'Marketing', 'Sales', 'Information Technology', 'Risk & Compliance', 'Supply Chain', 'Security', 'Corporate Affairs', 'HSE', 'Quality Assurance', 'Production', 'Legal', 'Management'];
const DESIGNATIONS = ['Managing Director', 'General Manager', 'Deputy General Manager', 'Head of Department', 'Manager', 'Senior Manager', 'Assistant Manager', 'Supervisor', 'Senior Supervisor', 'Team Lead', 'Senior Officer', 'Officer', 'Assistant Officer', 'Technician', 'Senior Technician', 'Operator', 'Senior Operator', 'Artisan', 'Assistant Artisan', 'Contractor', 'Intern/NYSC'];

const empty = { fullName: '', email: '', businessUnit: '', department: '', designation: '' };

export default function ObserversPage() {
  const { admin } = useAuth();
  const availablePlants = admin?.role === 'superadmin' ? BUS : (admin?.plants || []);
  const [observers, setObservers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [linkModal, setLinkModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [filterBusinessUnit, setFilterBusinessUnit] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetch = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/observers');
      setObservers(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setErrors({}); setModalOpen(true); };
  const openEdit = (obs) => { setEditing(obs); setForm({ fullName: obs.fullName, email: obs.email, businessUnit: obs.businessUnit, department: obs.department, designation: obs.designation }); setErrors({}); setModalOpen(true); };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    if (!form.businessUnit) e.businessUnit = 'Required';
    if (!form.department.trim()) e.department = 'Required';
    if (!form.designation.trim()) e.designation = 'Required';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/observers/${editing._id}`, form);
        setObservers(obs => obs.map(o => o._id === editing._id ? data : o));
        toast.success('Observer updated');
      } else {
        const { data } = await api.post('/observers', form);
        setObservers(obs => [data, ...obs]);
        toast.success('Observer created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving observer');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/observers/${id}`);
      setObservers(obs => obs.filter(o => o._id !== id));
      toast.success('Observer removed');
    } catch { toast.error('Failed to delete'); }
    setDeleteConfirm(null);
  };

  const getLink = async (obs) => {
    try {
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/submit/${obs.formSlug}`;
      setLinkModal({ name: obs.fullName, link });
    } catch { toast.error('Failed to get link'); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(ev => ({ ...ev, [k]: '' })); };

  const filteredObservers = observers.filter(obs => {
    const matchesName = obs.fullName.toLowerCase().includes(searchName.toLowerCase());
    const matchesBU = !filterBusinessUnit || obs.businessUnit === filterBusinessUnit;
    const matchesDept = !filterDepartment || obs.department === filterDepartment;
    return matchesName && matchesBU && matchesDept;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, filterBusinessUnit, filterDepartment]);

  const totalPages = Math.max(1, Math.ceil(filteredObservers.length / rowsPerPage));
  const pagedObservers = filteredObservers.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <div className="fade-in">
      <PageHeader
        title="Observers"
        subtitle="Manage registered observers and their unique form links"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="sm" icon={<RefreshCw size={13} />} onClick={fetch}>Refresh</Button>
            <Button size="sm" icon={<Plus size={13} />} onClick={openCreate}>Add Observer</Button>
          </div>
        }
      />

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--border)', backgroundColor: 'var(--bg-2)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <Input
              placeholder="Search by name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              style={{ height: 36 }}
            />
            <Select value={filterBusinessUnit} onChange={(e) => setFilterBusinessUnit(e.target.value)} style={{ height: 36 }}>
              <option value="">All Business Units</option>
              {availablePlants.map(b => <option key={b} value={b}>{b}</option>)}
            </Select>
            <Select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} style={{ height: 36 }}>
              <option value="">All Departments</option>
              {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
            </Select>
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 24 }}>
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />)}
          </div>
        ) : observers.length === 0 ? (
          <EmptyState icon={<Users size={32} />} title="No observers yet" description="Add observers to generate their unique form submission links" />
        ) : filteredObservers.length === 0 ? (
          <EmptyState icon={<Users size={32} />} title="No observers found" description="Try adjusting your search or filter criteria" />
        ) : (
          <>
          <div className="observers-table-wrap">
            <table className="observers-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Business Unit</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedObservers.map(obs => (
                  <tr key={obs._id}>
                    <td className="obs-name">{obs.fullName}</td>
                    <td className="obs-email">{obs.email}</td>
                    <td><Badge variant="accent">{obs.businessUnit}</Badge></td>
                    <td>{obs.department}</td>
                    <td>{obs.designation}</td>
                    <td><Badge variant={obs.active ? 'safe' : 'default'}>{obs.active ? 'Active' : 'Inactive'}</Badge></td>
                    <td>
                      <div className="obs-actions">
                        <button className="icon-btn" title="Copy form link" onClick={() => getLink(obs)}><Link2 size={14} /></button>
                        <button className="icon-btn" title="Edit" onClick={() => openEdit(obs)}><Pencil size={14} /></button>
                        <button className="icon-btn icon-btn--danger" title="Delete" onClick={() => setDeleteConfirm(obs)}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <div>{`Showing ${filteredObservers.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} - ${Math.min(currentPage * rowsPerPage, filteredObservers.length)} of ${filteredObservers.length}`}</div>
            <div className="pagination-controls">
              <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
              <button className="page-btn" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>Prev</button>
              <button className="page-btn page-btn--active" disabled>{currentPage}</button>
              <button className="page-btn" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
              <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
            </div>
          </div>
          </>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Observer' : 'Add Observer'}>
        <div className="modal-form">
          <Input label="Full Name" placeholder="John Adewale" value={form.fullName} onChange={set('fullName')} error={errors.fullName} />
          <Input label="Email Address" type="email" placeholder="john@ufl.com" value={form.email} onChange={set('email')} error={errors.email} />
          <Select label="Business Unit / Plant" value={form.businessUnit} onChange={set('businessUnit')} error={errors.businessUnit}>
            <option value="">{availablePlants.length ? 'Select business unit / plant' : 'No plants assigned'}</option>
            {availablePlants.map(b => <option key={b} value={b}>{b}</option>)}
          </Select>
          <Select label="Department" value={form.department} onChange={set('department')} error={errors.department}>
            <option value="">Select department</option>
            {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
          </Select>
          <Select label="Designation / Role" value={form.designation} onChange={set('designation')} error={errors.designation}>
            <option value="">Select designation</option>
            {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </Select>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>{editing ? 'Save Changes' : 'Create Observer'}</Button>
          </div>
        </div>
      </Modal>

      {/* Link Modal */}
      <Modal open={!!linkModal} onClose={() => setLinkModal(null)} title="Observer Form Link">
        {linkModal && (
          <div className="link-modal-body">
            <p className="link-modal-name">Form link for <strong>{linkModal.name}</strong></p>
            <p className="link-modal-sub">Share this unique link with the observer. They will use it to submit every observation.</p>
            <div className="link-box">
              <span className="link-text">{linkModal.link}</span>
              <button className="link-copy-btn" onClick={() => copyLink(linkModal.link)}>
                <Copy size={13} /> Copy
              </button>
            </div>
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setLinkModal(null)}>Close</Button>
              <Button icon={<Copy size={13} />} onClick={() => { copyLink(linkModal.link); setLinkModal(null); }}>Copy & Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Remove Observer">
        {deleteConfirm && (
          <div>
            <p style={{ fontSize: '0.83rem', color: 'var(--text-2)', marginBottom: 16 }}>
              Are you sure you want to remove <strong>{deleteConfirm.fullName}</strong>? Their form link will be deactivated. Submitted observations will remain.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirm._id)}>Remove Observer</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
